import { useState } from 'react'
import { AlertTriangle, Clock } from 'lucide-react'
import { Button } from '@shared/ui/Button'
import { usePlatform, prepareAsyncOpenTab } from '@shared/platform'
import {
  TRIBUTE_CANCEL_BOT_URL,
  TRIBUTE_CANCEL_BOT_WEB_URL,
} from '@shared/config/routes'
import { useAppStore } from '@/store/useAppStore'
import { useProfileQuery } from '@entities/user'
import {
  useSetAutoRenewMutation,
  useCancelReasonMutation,
  useSubscriptionPricesQuery,
  usePlanLookup,
  formatPrice,
  CancelAutoRenewConfirmModal,
  AutoRenewDisabledResultModal,
  isGraceVisible,
  ChangePlanModal,
  PlanChangedResultModal,
  useSetNextPlanMutation,
  formatPlanName,
  applyOverride,
} from '@features/subscription'
import {
  ProviderPickerModal,
  useBlockingSubscriptionQuery,
  useBuySubscriptionMutation,
  useDoubleSubmitGuard,
  usePaymentMethodsQuery,
  useStartPayment,
  type ProviderPickerSubmit,
} from '@features/payments'
import RecommendedChannels from '../RecommendedChannels'
import { PaymentMethodsBlock } from './PaymentMethodsBlock'
import { SubscriptionIssueBanner } from './SubscriptionIssueBanner'
import styles from './SubscriptionSection.module.css'

interface SubscriptionSectionProps {
  onSettingsClick: () => void
}

interface ActiveDateRowProps {
  formattedExpiresAt: string | null
  autoRenew: boolean
  showToggle: boolean
  toggleDisabled: boolean
  onToggle: () => void
  bonusDaysLabel?: string | null
}

const ActiveDateRow = ({
  formattedExpiresAt,
  autoRenew,
  showToggle,
  toggleDisabled,
  onToggle,
  bonusDaysLabel,
}: ActiveDateRowProps) => (
  <div className={styles.activeRow}>
    <div className={styles.activeColumn}>
      <span className={styles.activeLabel}>Активна до:</span>
      <span className={styles.activeDate}>
        {formattedExpiresAt ?? '—'}
        {bonusDaysLabel && (
          <span className={styles.bonusDays}> · {bonusDaysLabel}</span>
        )}
      </span>
    </div>
    {showToggle && (
      <div className={styles.activeColumn}>
        <span className={styles.activeLabel}>Автопродление</span>
        <button
          type="button"
          className={`${styles.autoRenewToggle} ${autoRenew ? styles.autoRenewToggleActive : ''}`}
          aria-pressed={autoRenew}
          aria-label="Автопродление"
          onClick={onToggle}
          disabled={toggleDisabled}
        >
          <span className={styles.autoRenewKnob} aria-hidden />
        </button>
      </div>
    )}
  </div>
)

const getAutoRenewErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409)
    return 'Нет сохранённой карты для автопродления. Сначала привяжите карту.'
  if (status === 502)
    return 'Провайдер не отвечает. Попробуйте ещё раз через минуту.'
  return 'Не удалось изменить автопродление. Попробуйте ещё раз.'
}

const getNextPlanErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409)
    return 'Сменить тариф можно только при включённом автопродлении.'
  if (status === 422) return 'Этот тариф сейчас недоступен. Выберите другой.'
  return 'Не удалось сменить тариф. Попробуйте ещё раз.'
}

const getBuyErrorText = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 409)
    return 'У вас уже есть активная подписка или незавершённый платёж.'
  if (status === 502)
    return 'Не удалось связаться с провайдером. Попробуйте ещё раз.'
  return 'Не удалось создать платёж. Попробуйте ещё раз.'
}

const SubscriptionSection = ({ onSettingsClick }: SubscriptionSectionProps) => {
  const subscription = useAppStore(s => s.subscription)
  // Грейс-период живёт только в profile.subscription (legacy-стор его не несёт).
  // Флаг грейса живёт в профиле, а дата окончания — в подписке, и обновляются
  // они независимо. Сверяем их, иначе экран противоречит сам себе: плашка
  // «подписка скоро закончится» над датой на год вперёд.
  const graceFlag = useAppStore(s => s.profile?.subscription?.grace_active === true)
  // Подписка оплачена, но доступ в боте не выдан — предупреждаем и подсказываем
  // действие. Как и грейс, живёт только в profile.subscription.
  const issueCanceled = useAppStore(
    s => s.profile?.subscription?.issue_canceled === true
  )
  const issueStatus = useAppStore(s => s.profile?.subscription?.issue_status)
  const hasActiveSubscription = useAppStore(s => s.hasActiveSubscription)
  const isActive = hasActiveSubscription()
  const graceActive = isGraceVisible(graceFlag, subscription.expiresAt)
  const setAutoRenewMutation = useSetAutoRenewMutation()
  const cancelReasonMutation = useCancelReasonMutation()
  const autoRenewError = setAutoRenewMutation.isError
    ? getAutoRenewErrorText(setAutoRenewMutation.error)
    : null
  const { lookup: planLookup } = usePlanLookup()
  const plan = planLookup(subscription.subscriptionInfoId)
  // Закреплённая цена приоритетнее тарифной: бэк фиксирует её при оформлении
  // и не меняет, даже если плановая цена поднялась. Если закреплённой нет —
  // fallback на текущую цену тарифа из справочника.
  const basePrice =
    subscription.renewalPrice != null
      ? subscription.renewalPrice
      : plan
        ? plan.price
        : null
  const baseCurrency =
    subscription.renewalCurrency ?? plan?.currency ?? 'RUB'
  // Разовая скидка на следующее списание — бэк отдаёт долю строкой ("0.15").
  // Парсим и клипуем в [0..1]; невалидное значение — без скидки.
  const parsedDiscount = (() => {
    const raw = subscription.nextChargeDiscount
    if (raw == null) return null
    const v = Number.parseFloat(raw)
    if (!Number.isFinite(v) || v <= 0) return null
    return Math.min(v, 1)
  })()
  const discountedPrice =
    basePrice != null && parsedDiscount != null
      ? Math.max(0, Math.round(basePrice * (1 - parsedDiscount)))
      : null
  const tariffSuffix = plan?.suffix ? ` / ${plan.suffix}` : ''
  const tariffRow = basePrice != null ? (
    <div className={styles.metaRow}>
      <span className={styles.metaLabel}>Тариф</span>
      <span className={styles.metaValue}>
        {discountedPrice != null && (
          <span className={styles.priceOld}>
            {formatPrice(basePrice, baseCurrency)}
          </span>
        )}
        <span>
          {formatPrice(discountedPrice ?? basePrice, baseCurrency)}
          {tariffSuffix}
        </span>
      </span>
    </div>
  ) : null
  const [isCancelOpen, setCancelOpen] = useState(false)
  // Сбор причины отмены для Tribute — отдельный поток: причина логируется,
  // затем редирект в Tribute Mini App (auto_renew наш бэк там не меняет).
  const [isTributeCancelOpen, setTributeCancelOpen] = useState(false)
  const [isResultOpen, setResultOpen] = useState(false)
  const [resultMode, setResultMode] = useState<'enabled' | 'disabled'>(
    'disabled'
  )

  // === Отложенная смена тарифа ===
  // Тариф читаем из профиля напрямую, а не из legacy-стора подписки: стор
  // заполняется в нескольких местах и этого поля не несёт, а профиль в react-query
  // уже загружен соседним блоком способов входа.
  const { data: profileData } = useProfileQuery()
  const nextPlanId = profileData?.subscription?.next_subscription_info_id ?? null
  const currentPlanId = subscription.subscriptionInfoId
  // Смена показывается только если она отличается от текущего тарифа: бэк
  // трактует «тот же план» как отсутствие смены, и повторять это в интерфейсе
  // значило бы пугать человека несуществующим изменением.
  const hasPendingPlanChange =
    nextPlanId != null && nextPlanId !== currentPlanId
  const nextPlan = planLookup(nextPlanId ?? undefined)

  const [isChangePlanOpen, setChangePlanOpen] = useState(false)
  const [planResult, setPlanResult] = useState<{
    planName: string | null
    priceText: string | null
  } | null>(null)
  const setNextPlanMutation = useSetNextPlanMutation()
  // Справочник тарифов нужен и для списка в модалке, и для названия уже
  // назначенного тарифа в карточке, поэтому запрашиваем его всегда.
  const { data: plansData } = useSubscriptionPricesQuery()

  const [isBuyOpen, setBuyOpen] = useState(false)
  const [buyError, setBuyError] = useState<string | null>(null)
  const buyMutation = useBuySubscriptionMutation()
  const guard = useDoubleSubmitGuard()
  const startPayment = useStartPayment()
  const platform = usePlatform()
  const { data: paymentMethodsData } = usePaymentMethodsQuery()
  const paymentMethods = paymentMethodsData?.methods ?? []
  // Карта для автопродления: основная, иначе первая привязанная.
  const autoRenewCard =
    paymentMethods.find(m => m.is_main) ?? paymentMethods[0] ?? null
  // Тоггл автопродления — только для Robokassa: в Tribute управление подпиской
  // живёт в их собственном Telegram-приложении, наш PATCH туда не дотягивается.
  const isRobokassa = subscription.provider === 'robokassa'
  const isTribute = subscription.provider === 'tribute'
  const hasMainCard = isRobokassa && autoRenewCard != null

  const openTributeManage = () => {
    platform.openLink('https://telegram.me/tribute/app')
  }

  const openTributeCancel = () => {
    cancelReasonMutation.reset()
    setTributeCancelOpen(true)
  }
  const closeTributeCancel = () => {
    if (cancelReasonMutation.isLoading) return
    setTributeCancelOpen(false)
  }
  // Сначала логируем причину, потом уводим в сценарий отмены в нашем боте:
  // в Tribute Mini App пользователи не понимали, что делать дальше. Сбор ОС не
  // должен блокировать отмену: при ошибке логирования всё равно открываем бота.
  const handleTributeCancelConfirm = (reason: string | null) => {
    // В вебе вкладку готовим синхронно по клику: после ответа мутации
    // user-gesture уже нет и браузер срежет popup. В Telegram блокировщика нет,
    // и ссылку должен обработать сам клиент — тогда бот откроется, а мини-апп
    // закроется, чего openLink не делает.
    const tab = platform.isWeb() ? prepareAsyncOpenTab(platform) : null
    cancelReasonMutation.mutate(
      // Бэк требует непустой reason — «без причины» шлём как явный текст.
      { reason: reason ?? 'Без причины' },
      {
        onSettled: () => {
          setTributeCancelOpen(false)
          // В вебе ведём в Telegram Web: там сценарий откроется даже без
          // установленного клиента. В Telegram — обычная t.me.
          if (tab) tab.complete(TRIBUTE_CANCEL_BOT_WEB_URL)
          else platform.openTelegramLink(TRIBUTE_CANCEL_BOT_URL)
        },
      }
    )
  }

  const handleToggleAutoRenew = () => {
    if (setAutoRenewMutation.isLoading) return
    if (subscription.autoRenew) {
      // Выключаем — открываем модалку подтверждения
      setAutoRenewMutation.reset()
      setCancelOpen(true)
    } else {
      // Включаем — бэкенду нужен payment_method_id, иначе 409 «нет карты».
      if (!autoRenewCard) return
      setAutoRenewMutation.reset()
      setAutoRenewMutation.mutate(
        { enabled: true, payment_method_id: autoRenewCard.payment_method_id },
        {
          onSuccess: () => {
            setResultMode('enabled')
            setResultOpen(true)
          },
        }
      )
    }
  }

  const openBuyModal = () => {
    if (isBuyBlocked) return
    setBuyError(null)
    buyMutation.reset()
    setBuyOpen(true)
  }
  const closeBuyModal = () => {
    if (buyMutation.isLoading) return
    setBuyError(null)
    buyMutation.reset()
    setBuyOpen(false)
  }
  const handleBuySubmit = ({
    planId,
    provider,
    saveCard,
    paymentMethodId,
  }: ProviderPickerSubmit) => {
    // Замок берём до всего остального: по isLoading второй тап проскакивал и
    // создавал второй счёт.
    if (!guard.tryStart()) return
    setBuyError(null)
    // Окно открываем синхронно по клику — после await mutate браузер блокирует popup.
    const tab = prepareAsyncOpenTab(platform)
    buyMutation.mutate(
      {
        subscription_info_id: planId,
        provider,
        save_card: saveCard,
        payment_method_id: paymentMethodId,
      },
      {
        onSuccess: invoice => {
          setBuyOpen(false)
          startPayment(invoice, tab)
        },
        onError: error => {
          tab.abort()
          setBuyError(getBuyErrorText(error))
        },
        onSettled: (_invoice, error) => guard.finish(!error),
      }
    )
  }

  const openChangePlan = () => {
    setNextPlanMutation.reset()
    setChangePlanOpen(true)
  }
  const closeChangePlan = () => {
    if (setNextPlanMutation.isLoading) return
    setChangePlanOpen(false)
  }
  const handleChangePlanSubmit = (planId: number | null) => {
    if (setNextPlanMutation.isLoading) return
    setNextPlanMutation.mutate(
      { subscription_info_id: planId },
      {
        onSuccess: () => {
          setChangePlanOpen(false)
          const plan = planId != null ? planLookup(planId) : undefined
          const price = plan ? applyOverride(plan, plansData?.override) : null
          setPlanResult({
            planName: plan ? formatPlanName(plan) : null,
            priceText: price
              ? formatPrice(price.finalPrice, price.finalCurrency)
              : null,
          })
        },
      }
    )
  }

  const closeCancelModal = () => {
    if (setAutoRenewMutation.isLoading) return
    setCancelOpen(false)
  }
  const handleConfirmCancel = (reason: string | null) => {
    setAutoRenewMutation.mutate(
      { enabled: false, reason },
      {
        onSuccess: () => {
          setCancelOpen(false)
          setResultMode('disabled')
          setResultOpen(true)
        },
      }
    )
  }

  const formattedExpiresAt = subscription.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString('ru-RU')
    : null
  // У Tribute с бонусными днями реальная дата списания (`tribute_payment_date`)
  // не совпадает с датой окончания подписки (`subscription_end_timestamp`).
  // Если поле заполнено — показываем обе даты раздельно.
  const formattedTributePaymentDate = subscription.tributePaymentDate
    ? new Date(subscription.tributePaymentDate).toLocaleDateString('ru-RU')
    : null
  const nextChargeDate = formattedTributePaymentDate ?? formattedExpiresAt
  // Бонусные дни = (subscription_end_timestamp - tribute_payment_date) в днях.
  // Считаем только если обе даты есть, иначе бонусов нет.
  const bonusDays = (() => {
    if (!subscription.tributePaymentDate || !subscription.expiresAt) return 0
    const paymentMs = new Date(subscription.tributePaymentDate).getTime()
    const endMs = new Date(subscription.expiresAt).getTime()
    if (!Number.isFinite(paymentMs) || !Number.isFinite(endMs)) return 0
    const diffDays = Math.round((endMs - paymentMs) / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : 0
  })()
  const pluralizeDays = (n: number) => {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return 'день'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня'
    return 'дней'
  }
  const bonusDaysLabel =
    bonusDays > 0
      ? `с учётом ${bonusDays} ${pluralizeDays(bonusDays)} в подарок`
      : null

  // Автоплатёж в ожидании проведения блокирует покупку подписки (бэк ответит
  // 409). Пока он есть — показываем жёлтую плашку «подождите» во всех состояниях.
  const { data: blockingPayment } = useBlockingSubscriptionQuery()
  // Пока автоплатёж в обработке, покупка подписки запрещена: бэк всё равно
  // ответит 409, поэтому не даём открыть модалку выбора тарифа вовсе.
  const isBuyBlocked = blockingPayment != null
  const blockingPaymentBanner = blockingPayment ? (
    <div className={styles.graceBanner}>
      <Clock size={18} className={styles.graceBannerIcon} aria-hidden />
      <p className={styles.graceBannerText}>
        Автоплатёж в процессе. Пожалуйста, подождите — как только он будет
        проведён, подписка обновится автоматически.
      </p>
    </div>
  ) : null

  // Плашка грейс-периода: подписка скоро закончится. Показываем в обоих
  // активных состояниях; текст для Tribute — про баланс карты.
  const graceBanner = graceActive ? (
    <div className={styles.graceBanner}>
      <AlertTriangle size={18} className={styles.graceBannerIcon} aria-hidden />
      <p className={styles.graceBannerText}>
        {isTribute
          ? 'Подписка скоро закончится. Проверьте баланс карты — в течение недели мы повторим попытки продлить её.'
          : 'Подписка скоро закончится. Продлите её, чтобы не потерять доступ к контенту.'}
      </p>
    </div>
  ) : null

  // Доступ не выдан — показываем рядом с грейс-плашкой, в обоих активных
  // состояниях: подписка при этом оплачена и активна.
  const issueBanner = issueCanceled ? (
    <SubscriptionIssueBanner status={issueStatus} />
  ) : null

  // Отметка об отложенной смене: человек должен видеть, что сейчас у него один
  // тариф, а со следующего списания будет другой.
  const nextPlanRow = hasPendingPlanChange ? (
    <div className={styles.metaRow}>
      <span className={styles.metaLabel}>Следующий тариф</span>
      <span className={styles.metaValue}>
        {nextPlan ? formatPlanName(nextPlan) : 'Другой тариф'}
        {nextChargeDate && (
          <span className={styles.priceDate}> · с {nextChargeDate}</span>
        )}
      </span>
    </div>
  ) : null

  const nextPlanModals = (
    <>
      {isChangePlanOpen && (
        <ChangePlanModal
          plans={plansData?.subscriptions ?? []}
          override={plansData?.override}
          currentPlanId={currentPlanId}
          nextPlanId={hasPendingPlanChange ? nextPlanId : null}
          nextChargeDate={nextChargeDate}
          isSubmitting={setNextPlanMutation.isLoading}
          errorText={
            setNextPlanMutation.isError
              ? getNextPlanErrorText(setNextPlanMutation.error)
              : null
          }
          onSubmit={handleChangePlanSubmit}
          onClose={closeChangePlan}
        />
      )}

      {planResult && (
        <PlanChangedResultModal
          planName={planResult.planName}
          priceText={planResult.priceText}
          nextChargeDate={nextChargeDate}
          onClose={() => setPlanResult(null)}
        />
      )}
    </>
  )

  // Модалки автопродления держим вне веток состояния: смена autoRenew
  // меняет ветку (early return), и если рендерить их только в одной —
  // result-модалка пропадёт сразу после успешной мутации.
  const autoRenewModals = (
    <>
      {isCancelOpen && (
        <CancelAutoRenewConfirmModal
          isSubmitting={setAutoRenewMutation.isLoading}
          errorText={autoRenewError}
          onConfirm={handleConfirmCancel}
          onClose={closeCancelModal}
        />
      )}

      {isTributeCancelOpen && (
        <CancelAutoRenewConfirmModal
          isSubmitting={cancelReasonMutation.isLoading}
          onConfirm={handleTributeCancelConfirm}
          onClose={closeTributeCancel}
        />
      )}

      {isResultOpen && (
        <AutoRenewDisabledResultModal
          expiresAtFormatted={formattedExpiresAt}
          mode={resultMode}
          onClose={() => setResultOpen(false)}
        />
      )}
    </>
  )

  // Состояние «Подписка неактивна»: либо нет подписки, либо `is_active=false`,
  // либо срок уже прошёл (вычисляется в `hasActiveSubscription`).
  if (!isActive) {
    return (
      <>
        <div className={`profile-section ${styles.section}`}>
          <h2 className="section-title">Подписка</h2>
          {blockingPaymentBanner}
          <p className={styles.statusText}>Подписка неактивна</p>
          <p className={styles.description}>
            Оформите подписку, чтобы получить доступ ко всему контенту.
          </p>
        </div>
        <PaymentMethodsBlock />

        <Button onClick={openBuyModal} block disabled={isBuyBlocked}>
          Оформить подписку
        </Button>
        <button className="support-button" onClick={onSettingsClick}>
          Тех. поддержка
        </button>
        <RecommendedChannels defaultOpen={true} />

        {isBuyOpen && (
          <ProviderPickerModal
            plans={plansData?.subscriptions ?? []}
            override={plansData?.override}
            paymentMethods={paymentMethods}
            onSubmit={handleBuySubmit}
            onClose={closeBuyModal}
            isSubmitting={buyMutation.isLoading}
            errorText={buyError}
          />
        )}
        {autoRenewModals}
      </>
    )
  }

  // Состояние «Подписка активна, автопродление отключено».
  // Подписка работает до `expiresAt`, но дальше не продлится — нужна кнопка «Возобновить».
  if (subscription.autoRenew === false) {
    return (
      <>
        <div className={`profile-section ${styles.section}`}>
          <h2 className="section-title">Подписка</h2>
          {blockingPaymentBanner}
          {graceBanner}
          {issueBanner}
          <ActiveDateRow
            formattedExpiresAt={formattedExpiresAt}
            autoRenew={false}
            showToggle={hasMainCard}
            toggleDisabled={setAutoRenewMutation.isLoading}
            onToggle={handleToggleAutoRenew}
            bonusDaysLabel={bonusDaysLabel}
          />
          {/* У Tribute с бонусными днями дата реального списания приходит отдельно
              и не равна дате окончания подписки — показываем её здесь. */}
          {formattedTributePaymentDate && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Следующее списание</span>
              <span className={styles.metaValue}>
                {formattedTributePaymentDate}
              </span>
            </div>
          )}
          {tariffRow}
          {!isTribute && (
            <p className={styles.description}>
              Автопродление отключено. После окончания срока доступ к подписке прекратится.
            </p>
          )}
          {autoRenewError && <p className={styles.error}>{autoRenewError}</p>}
        </div>
        <PaymentMethodsBlock />
        {isTribute && (
          <>
            <button className="support-button" onClick={openTributeManage}>
              Управлять подпиской
            </button>
            <button
              className={`support-button ${styles.cancelButton}`}
              onClick={openTributeCancel}
            >
              Отмена подписки
            </button>
          </>
        )}
        <button className="support-button" onClick={onSettingsClick}>
          Тех. поддержка
        </button>
        <RecommendedChannels defaultOpen={true} />
        {autoRenewModals}
      </>
    )
  }

  // Состояние «Подписка активна, автопродление включено».
  return (
    <>
      <div className={`profile-section ${styles.section}`}>
        <h2 className="section-title">Подписка</h2>

        {blockingPaymentBanner}
        {graceBanner}
        {issueBanner}

        <ActiveDateRow
          formattedExpiresAt={formattedExpiresAt}
          autoRenew={true}
          showToggle={hasMainCard}
          toggleDisabled={setAutoRenewMutation.isLoading}
          onToggle={handleToggleAutoRenew}
        />

        {tariffRow}

        {nextPlanRow}

        {nextChargeDate && (
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Следующее списание</span>
            <span className={styles.metaValue}>
              {discountedPrice != null && basePrice != null ? (
                <>
                  <span className={styles.priceOld}>
                    {formatPrice(basePrice, baseCurrency)}
                  </span>
                  <span>{formatPrice(discountedPrice, baseCurrency)}</span>
                  <span className={styles.priceDate}> · {nextChargeDate}</span>
                </>
              ) : (
                nextChargeDate
              )}
            </span>
          </div>
        )}

        {formattedTributePaymentDate && formattedExpiresAt && (
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Подписка активна до</span>
            <span className={styles.metaValue}>
              {formattedExpiresAt}
              {bonusDaysLabel && (
                <span className={styles.bonusDays}> · {bonusDaysLabel}</span>
              )}
            </span>
          </div>
        )}

        {autoRenewError && <p className={styles.error}>{autoRenewError}</p>}
      </div>
      <PaymentMethodsBlock />

      {isTribute && (
        <>
          <button className="support-button" onClick={openTributeManage}>
            Управлять подпиской
          </button>
          <button
            className={`support-button ${styles.cancelButton}`}
            onClick={openTributeCancel}
          >
            Отмена подписки
          </button>
        </>
      )}
      {/* Смена тарифа живёт только здесь: у Tribute тарифом управляет их
          портал, а без автопродления отложенной смене негде сработать —
          в этой ветке автопродление заведомо включено. */}
      {isRobokassa && (
        <button className="support-button" onClick={openChangePlan}>
          Сменить тариф
        </button>
      )}
      <button className="support-button" onClick={onSettingsClick}>
        Тех. поддержка
      </button>
      <RecommendedChannels defaultOpen={true} />
      {autoRenewModals}
      {nextPlanModals}
    </>
  )
}

export default SubscriptionSection
